import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { resource } from '@util/resource';
import { Payment, PaymentLink } from '@interfaces/dws/payment';
import { Sale, SaleRow } from '@interfaces/dws/sale';
import { map, switchMap } from 'rxjs/operators';
import { Page } from '@interfaces/dws/page';

const PAYMENT_LINK_LOCALES = ['it', 'en', 'de', 'fr', 'es', 'pt', 'el', 'hr'] as const;

export function resolvePaymentLinkLocale(languageIso?: string): string {
  if (!languageIso) return 'en';
  const normalized = languageIso.trim().toLowerCase().split(/[-_]/)[0];
  return (PAYMENT_LINK_LOCALES as readonly string[]).includes(normalized) ? normalized : 'en';
}

@Injectable({
  providedIn: 'root'
})
export class WSPaymentsService {

  constructor() { }

  /**
   * Creates a payment link for a sale.
   * @param saleRows Sale rows (entityType, entityId, amount, etc.)
   * @param contactId Optional contact ID
   * @param wineryId Optional winery ID - MUST be passed when winery has Dwspay to ensure Dwspay payment intent
   *                  (with on_behalf_of) is used instead of legacy Stripe. Required for reservation popup.
   * @param locale Optional reservation/checkout language (WS allow-list only)
   * @param currency Optional ISO presentment currency (e.g. USD). Falls back to eur if omitted.
   */
  createPaymentLink(
    saleRows: SaleRow[],
    contactId?: number,
    wineryId?: string,
    locale?: string,
    currency?: string,
  ): Observable<[PaymentLink, Sale]> {
    return resource('crm://wspayments/sales')
      .post<Sale>({ saleRows, contactId })
      .pipe(
        switchMap(sale => {
          const payload: any = {
            locale: resolvePaymentLinkLocale(locale),
          };
          if (wineryId) payload.wineryId = wineryId;
          if (currency) payload.currency = String(currency).toLowerCase();
          return resource('crm://wspayments/sales/id/payment-links')
            .params({ id: sale.id })
            .post<PaymentLink>(payload)
            .pipe(map(paymentLink => [paymentLink, sale] as [PaymentLink, Sale]));
        })
      );
  }

  createDwspayPaymentIntent(payload: ManualDwspayPaymentIntentRequest): Observable<ManualDwspayPaymentIntentResponse> {
    return resource('crm://wspayments/dwspay/payment-intent')
      .post<ManualDwspayPaymentIntentResponse>(payload);
  }

  getPaymentLink(filters: PaymentLinkFilters): Observable<Page<PaymentLink>> {
    return resource('crm://wspayments/payment-links')
      .params({ ...filters })
      .get();
  }

  getPaymentLinkById(id: string): Observable<PaymentLink> {
    return resource('crm://wspayments/payment-links/id')
      .params({ id })
      .get();
  }

  getPaymentLinkEmbeddedSetup(
    paymentLinkId: string,
    wineryId: string,
  ): Observable<PaymentLinkEmbeddedSetup> {
    return resource('crm://wspayments/accounts/payment-links/id/embedded-setup')
      .params({ id: paymentLinkId, wineryId })
      .get<PaymentLinkEmbeddedSetup>();
  }

  getPaymentById(id: string): Observable<Payment> {
    return resource('crm://wspayments/payments/id')
      .params({ id })
      .get();
  }

  /**
   * GET wspayments/dwspay/winesuite/payment-intent/{id}?wineryId=
   * Expanded PaymentIntent (+ charge); Wine Suite CRM path (logic shared with terminal implementation).
   */
  getDwspayWinesuitePaymentIntentDetails(
    wineryId: string,
    paymentIntentId: string
  ): Observable<DwspayPaymentIntentDetailsResponse> {
    return resource('crm://wspayments/dwspay/winesuite-payment-intent/id')
      .params({ id: paymentIntentId, wineryId })
      .get<DwspayPaymentIntentDetailsResponse>();
  }

  /**
   * WS-2498: paginated Wine Suite payments list via GET /dwspay/winesuite/payments.
   * Query params match {@code WinesuitePaymentListFilter} (unix seconds, amount in euros).
   */
  getWinesuitePayments(
    wineryId: string,
    page: number,
    size: number,
    query: WinesuitePaymentsListQuery = {},
  ): Observable<WinesuitePaymentsPage> {
    const params: { [k: string]: string } = {
      wineryId,
      page: String(page),
      size: String(size),
    };
    const search = query.search?.trim();
    if (search && search.length >= 2) {
      params.search = search;
    }
    if (query.status) {
      params.status = query.status;
    }
    if (query.customerName?.trim()) {
      params.customerName = query.customerName.trim();
    }
    if (query.customerEmail?.trim()) {
      params.customerEmail = query.customerEmail.trim();
    }
    if (query.saleId?.trim()) {
      params.saleId = query.saleId.trim();
    }
    if (query.mode) {
      params.mode = query.mode === 'card_not_present' ? 'card' : query.mode;
    }
    if (query.origin?.trim()) {
      params.origin = query.origin.trim();
    }
    if (query.amountMin != null && !Number.isNaN(query.amountMin)) {
      params.amountMin = String(query.amountMin);
    }
    if (query.amountMax != null && !Number.isNaN(query.amountMax)) {
      params.amountMax = String(query.amountMax);
    }
    if (query.dateFrom != null) {
      params.dateFrom = String(query.dateFrom);
    }
    if (query.dateTo != null) {
      params.dateTo = String(query.dateTo);
    }
    if (query.sortDir) {
      params.sortDir = query.sortDir;
    }

    return resource('crm://wspayments/dwspay/winesuite/payments')
      .params(params)
      .get<WinesuitePaymentsPage>();
  }

  /**
   * WS-2666: paginated Wine Suite payouts list via POST /dwspay/payouts/query.
   */
  getWinesuitePayouts(
    wineryId: string,
    page: number,
    size: number,
    query: WinesuitePayoutsListQuery = {},
  ): Observable<WinesuitePayoutsPage> {
    // Same contract as payments: page/size + optional filters. Dates only on the request
    // (Stripe created range); do not duplicate them inside filter.
    const { dateFrom, dateTo } = this.resolveOptionalPaymentsListDateRange(query);
    const filter: DwspayPayoutsFilter = {};
    const searchText = query.search?.trim();
    if (searchText && searchText.length >= 2) {
      filter.searchText = searchText;
    }
    if (query.payoutId?.trim()) {
      filter.payoutId = query.payoutId.trim();
    }
    if (query.destination?.trim()) {
      filter.destination = query.destination.trim();
    }
    if (query.status) {
      filter.status = query.status;
    }
    if (query.method) {
      filter.method = query.method;
    }
    if (query.amountMin != null && !Number.isNaN(query.amountMin)) {
      filter.amountMin = Math.round(query.amountMin * 100);
    }
    if (query.amountMax != null && !Number.isNaN(query.amountMax)) {
      filter.amountMax = Math.round(query.amountMax * 100);
    }

    const body: {
      wineryId: string;
      page: number;
      count: number;
      dateFrom?: string;
      dateTo?: string;
      sort: string;
      filter: DwspayPayoutsFilter;
    } = {
      wineryId,
      page,
      count: size,
      sort: query.sortDir ?? 'desc',
      filter,
    };
    if (dateFrom) {
      body.dateFrom = dateFrom;
    }
    if (dateTo) {
      body.dateTo = dateTo;
    }

    return resource('crm://wspayments/dwspay/payouts/query')
      .post<WinesuitePayoutsPageResponse>(body)
      .pipe(map(response => ({
        payouts: response?.content ?? [],
        page: response?.page ?? 0,
        size: response?.count ?? size,
        totalElements: response?.total ?? 0,
      })));
  }

  /**
   * WS-2666: payout detail for the custom Bonifici modal.
   */
  getWinesuitePayoutDetail(wineryId: string, payoutId: string): Observable<WinesuitePayoutDetail> {
    return resource('crm://wspayments/dwspay/payouts/id')
      .params({ id: payoutId, wineryId })
      .get<WinesuitePayoutDetail>();
  }

  /**
   * WS-2498: payment detail for custom modals and refund embedded flow.
   */
  getWinesuitePaymentIntent(
    wineryId: string,
    paymentIntentId: string,
  ): Observable<DwspayPaymentIntentDetails> {
    return this.getDwspayWinesuitePaymentIntentDetails(wineryId, paymentIntentId).pipe(
      map(details => ({
        paymentIntent: details.paymentIntent,
        id: details.id,
        amount: details.amount,
        currency: details.currency,
        connectPaymentId: details.connectPaymentId,
        applicationFeeTotal: details.applicationFeeTotal,
        commissioneWspayments: details.commissioneWspayments,
        commissioneDivineaCom: details.commissioneDivineaCom,
        status: details.status,
        amountRefunded: details.amountRefunded ?? details.amount_refunded ?? 0,
        refunds: details.refunds ?? [],
        created: details.created,
        settlementAmount: details.settlementAmount,
        settlementCurrency: details.settlementCurrency,
        settlementFee: details.settlementFee,
        exchangeRate: details.exchangeRate,
      })),
    );
  }

  private resolveOptionalPaymentsListDateRange(query: { dateFrom?: number; dateTo?: number }): {
    dateFrom?: string;
    dateTo?: string;
  } {
    return {
      dateFrom: query.dateFrom != null ? new Date(query.dateFrom * 1000).toISOString() : undefined,
      dateTo: query.dateTo != null ? new Date(query.dateTo * 1000).toISOString() : undefined,
    };
  }

  private buildPaymentsQueryFilter(
    query: WinesuitePaymentsListQuery,
    dateFromIso?: string,
    dateToIso?: string,
  ): DwspayPaymentsFilter {
    const filter: DwspayPaymentsFilter = {};
    if (dateFromIso) {
      filter.dateFrom = dateFromIso;
    }
    if (dateToIso) {
      filter.dateTo = dateToIso;
    }

    const searchText = query.search?.trim();
    if (searchText && searchText.length >= 2) {
      filter.searchText = searchText;
    }
    if (query.status) {
      filter.status = this.mapListStatusToApi(query.status);
    }
    if (query.customerName?.trim()) {
      filter.customerName = query.customerName.trim();
    }
    if (query.customerEmail?.trim()) {
      filter.customerEmail = query.customerEmail.trim();
    }
    if (query.saleId?.trim()) {
      filter.saleId = query.saleId.trim();
    }
    if (query.mode) {
      filter.transactionMode = query.mode === 'card' ? 'card_not_present' : query.mode;
    }
    if (query.origin?.trim()) {
      filter.origin = query.origin.trim();
    }
    if (query.amountMin != null && !Number.isNaN(query.amountMin)) {
      filter.amountMin = Math.round(query.amountMin * 100);
    }
    if (query.amountMax != null && !Number.isNaN(query.amountMax)) {
      filter.amountMax = Math.round(query.amountMax * 100);
    }

    return filter;
  }

  private mapListStatusToApi(status: string): string {
    switch (status) {
      case 'succeeded':
      case 'failed':
      case 'refunded':
      case 'pending':
        return status;
      case 'partially_refunded':
        return 'partially_refunded';
      default:
        return status;
    }
  }

  private adaptPaymentsQueryResponse(response: DwspayPaymentsQueryResponse): WinesuitePaymentsPage {
    const rows = response?.content ?? [];
    return {
      payments: rows.map(row => this.adaptPaymentRowToSummary(row)),
      page: response?.page ?? 0,
      size: response?.count ?? rows.length,
      totalElements: response?.total ?? rows.length,
    };
  }

  private adaptPaymentRowToSummary(row: DwspayPaymentQueryRow): DwspayPaymentSummary {
    const metadata: Record<string, string> = {
      customer_name: row.customer_name ?? '',
      customer_email: row.customer_email ?? '',
      sale_id: row.sale_id ?? '',
      entity_type: row.entity_type ?? '',
      origin: row.origin ?? '',
    };

    let paymentMethodType = (row.payment_method_type ?? '').trim();
    if (!paymentMethodType) {
      if (row.transaction_mode === 'card_present') {
        paymentMethodType = 'card_present';
      } else if (row.transaction_mode === 'card_not_present') {
        paymentMethodType = 'card';
      }
    }

    return {
      id: row.stripe_id ?? '',
      amount: row.amount ?? 0,
      amountRefunded: row.amount_refunded ?? 0,
      currency: row.currency ?? 'eur',
      status: row.status ?? '',
      paymentMethodType,
      metadata,
      created: row.date != null ? row.date * 1000 : undefined,
      applicationFeeTotal: row.total_fee ?? 0,
      commissioneWspayments: row.wspayments_fee ?? 0,
      commissioneDivineaCom: row.divinea_fee ?? 0,
    };
  }

  getPayments(filters: { saleEntityType?: string, saleEntityId?: string }): Observable<Page<Payment>> {
    return resource('crm://wspayments/payments')
      .params(filters)
      .get();
  }

  markPaymentLinkAsSent(id: string, sentTo: string): Observable<void> {
    return resource('crm://wspayments/payment-links/id/send')
      .params({ id })
      .post({ sentTo });
  }

  regeneratePaymentLink(id: string, payload: RegeneratePaymentLinkRequest): Observable<PaymentLink> {
    return resource('crm://wspayments/payment-links/id/regenerate')
      .params({ id })
      .post<PaymentLink>(payload);
  }

  createManualPaymentLink(payload: ManualPaymentLinkRequest): Observable<PaymentLink> {
    return resource('crm://wspayments/payment-links/manual')
      .post<PaymentLink>(payload);
  }

  getPayment(filters: PaymentFilters): Observable<Page<Payment>> {
    return resource('crm://wspayments/payments')
      .params({ ...filters })
      .get();
  }

  /**
   * Returns the list of active terminals (readers) registered for the given winery.
   * Same data as used by POS (wspayments/dwspay/terminal/terminals).
   */
  getTerminals(wineryId: string): Observable<WspaymentsTerminalListItem[]> {
    return resource('crm://wspayments/dwspay/terminal/terminals')
      .params({ wineryId })
      .get<WspaymentsTerminalListItem[]>();
  }

  /** CloudPRNT thermal printers (`wspos_thermal_printers`). */
  listThermalPrinters(wineryId: string, pointOfSaleId?: string): Observable<WsposThermalPrinter[]> {
    const params: { wineryId: string; pointOfSaleId?: string } = { wineryId };
    if (pointOfSaleId) {
      params.pointOfSaleId = pointOfSaleId;
    }
    return resource('crm://wspayments/dwspay/wspos/thermal-printers')
      .params(params)
      .get<WsposThermalPrinter[]>();
  }

  createThermalPrinter(body: CreateWsposThermalPrinterRequest): Observable<WsposThermalPrinterCredentialsResponse> {
    return resource('crm://wspayments/dwspay/wspos/thermal-printers')
      .post<WsposThermalPrinterCredentialsResponse>(body);
  }

  updateThermalPrinter(id: string, body: UpdateWsposThermalPrinterRequest): Observable<WsposThermalPrinter> {
    return resource('crm://wspayments/dwspay/wspos/thermal-printers/id')
      .params({ id })
      .put<WsposThermalPrinter>(body);
  }

  rotateThermalPrinterCredentials(
    id: string,
    body?: { rotateUsername?: boolean; rotatePassword?: boolean },
  ): Observable<WsposThermalPrinterCredentialsResponse> {
    return resource('crm://wspayments/dwspay/wspos/thermal-printers/id/rotate-credentials')
      .params({ id })
      .post<WsposThermalPrinterCredentialsResponse>(body || {});
  }

  deactivateThermalPrinter(id: string): Observable<{ ok: boolean }> {
    return resource('crm://wspayments/dwspay/wspos/thermal-printers/id')
      .params({ id })
      .delete() as Observable<{ ok: boolean }>;
  }

  /** Enqueue Wine Suite CloudPRNT test page (Star picks up on next poll). */
  testPrintThermalPrinter(id: string): Observable<{ jobId: string; printerId: string; status: string; touchpoint?: string }> {
    return resource('crm://wspayments/dwspay/wspos/thermal-printers/id/test-print')
      .params({ id })
      .post<{ jobId: string; printerId: string; status: string; touchpoint?: string }>({ source: 'crm-test' });
  }

  enqueueCloudPrntPrintJob(body: {
    printerId: string;
    type: string;
    identifier: string;
    lang?: string;
    paymentType?: string;
    wineryId?: string;
    source?: string;
  }): Observable<{ jobId: string; printerId: string; status: string }> {
    return resource('crm://wspayments/dwspay/terminal/print-jobs')
      .post(body);
  }

  /**
   * WS-2665: unified advanced export (payments + payout info per row).
   */
  queryAdvancedExport(params: {
    wineryId: string;
    dateFrom: string;
    dateTo: string;
    mode: AdvancedExportMode;
    /** WS-2665: active tab — which payment rows to fetch (same 18-column CSV either way). */
    exportScope?: AdvancedExportScope;
    /** WS-3452: enrich payment rows with bonifico columns when true. */
    includePayout?: boolean;
    payoutId?: string;
    /** Full export range for payout lookup (reused across chunks server-side). */
    payoutLookupFrom?: string;
    payoutLookupTo?: string;
  }): Observable<AdvancedExportResponse> {
    return resource('crm://wspayments/dwspay/billing/report/advanced-export')
      .post<AdvancedExportResponse>(params);
  }

  /**
   * Retrieves Stripe Reader details for a given terminalId (tmr_...),
   * including serial_number, device_type, status, label, etc.
   */
  getTerminalReader(terminalId: string, wineryId: string): Observable<TerminalReaderDetail> {
    return resource('crm://wspayments/dwspay/terminal/terminals/terminalId/reader')
      .params({ terminalId, wineryId })
      .get<TerminalReaderDetail>();
  }

  private appendWinesuiteListQueryParams(
    params: Record<string, string>,
    query?: WinesuitePaymentsListQuery,
  ): void {
    if (!query) {
      return;
    }
    this.putQueryParam(params, 'search', query.search);
    this.putQueryParam(params, 'status', query.status);
    this.putQueryParam(params, 'customerName', query.customerName);
    this.putQueryParam(params, 'customerEmail', query.customerEmail);
    this.putQueryParam(params, 'saleId', query.saleId);
    this.putQueryParam(params, 'mode', query.mode);
    this.putQueryParam(params, 'origin', query.origin);
    this.putQueryParam(params, 'sortDir', query.sortDir);
    if (query.amountMin != null && !Number.isNaN(query.amountMin)) {
      params.amountMin = String(query.amountMin);
    }
    if (query.amountMax != null && !Number.isNaN(query.amountMax)) {
      params.amountMax = String(query.amountMax);
    }
    if (query.dateFrom != null) {
      params.dateFrom = String(query.dateFrom);
    }
    if (query.dateTo != null) {
      params.dateTo = String(query.dateTo);
    }
  }

  private putQueryParam(
    params: Record<string, string>,
    key: string,
    value?: string | null,
  ): void {
    if (value == null) {
      return;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return;
    }
    params[key] = encodeURIComponent(trimmed);
  }

  /**
   * Creates a subscription for a contact
   * @param subscriptionData - Subscription creation data
   */
  createSubscription(subscriptionData: {
    wineryId: string;
    contactId: number;
    customerClassId: string;
    paymentMethodId: string;
    amountCents?: number;
    currency?: string;
    interval?: 'month' | 'year' | 'week' | 'day';
    intervalCount?: number;
    metadata?: { [key: string]: string };
  }): Observable<{ subscriptionId: string; status: string; paymentMethodId: string }> {
    return resource('crm://wspayments/subscriptions')
      .post<{ subscriptionId: string; status: string; paymentMethodId: string }>(subscriptionData);
  }

  /**
   * Creates a subscription from a Terminal PaymentIntent (Option 3 - easiest)
   * This endpoint automatically extracts the generated_card from the PaymentIntent
   * and creates the subscription. Use this when you have a completed PaymentIntent
   * from Terminal that was created with setup_future_usage.
   * 
   * @param subscriptionData - Subscription creation data with paymentIntentId
   */
  createSubscriptionFromTerminalPayment(subscriptionData: {
    wineryId: string;
    contactId: number;
    customerClassId: string;
    paymentIntentId: string; // From completed Terminal PaymentIntent
    amountCents: number;
    currency?: string;
    interval?: 'month' | 'year' | 'week' | 'day';
    intervalCount?: number;
    metadata?: { [key: string]: string };
  }): Observable<{ subscriptionId: string; status: string; paymentMethodId: string }> {
    return resource('crm://wspayments/subscriptions/from-terminal-payment')
      .post<{ subscriptionId: string; status: string; paymentMethodId: string }>(subscriptionData);
  }

  /**
   * Gets subscription by ID
   */
  getSubscription(subscriptionId: string): Observable<{
    subscriptionId: string;
    status: string;
    contactId: number;
    customerClassId: string;
    paymentMethodId: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
  }> {
    return resource('crm://wspayments/subscriptions/id')
      .params({ id: subscriptionId })
      .get();
  }

  /**
   * Cancels a subscription
   */
  cancelSubscription(subscriptionId: string, wineryId: string): Observable<{ success: boolean }> {
    return resource('crm://wspayments/subscriptions/id/cancel')
      .params({ id: subscriptionId })
      .post<{ success: boolean }>({ wineryId });
  }

  /**
   * Creates a subscription payment link
   * @param subscriptionLinkData - Subscription payment link creation data
   */
  createSubscriptionPaymentLink(subscriptionLinkData: {
    saleRows: SaleRow[];
    contactId: number;
    amountCents: number;
    currency: string;
    interval: 'month' | 'year' | 'week' | 'day';
    intervalCount?: number;
    end?: string; // ISO date string for subscription end date
    wineryId: string;
  }): Observable<[PaymentLink, Sale]> {
    // First create the sale with contactId
    const salePayload: any = {
      saleRows: subscriptionLinkData.saleRows,
      contactId: subscriptionLinkData.contactId
    };

    return resource('crm://wspayments/sales')
      .post<Sale>(salePayload)
      .pipe(
        switchMap(sale => {
          // Then create the payment link with subscription parameters
          const paymentLinkPayload: any = {
            amount: subscriptionLinkData.amountCents,
            currency: subscriptionLinkData.currency,
            interval: subscriptionLinkData.interval,
            intervalCount: subscriptionLinkData.intervalCount || 1,
            contactId: subscriptionLinkData.contactId,
            wineryId: subscriptionLinkData.wineryId
          };

          if (subscriptionLinkData.end) {
            paymentLinkPayload.end = subscriptionLinkData.end;
          }

          return resource('crm://wspayments/sales/id/payment-links')
            .params({ id: sale.id })
            .post<PaymentLink>(paymentLinkPayload)
            .pipe(map(paymentLink => [paymentLink, sale] as [PaymentLink, Sale]));
        })
      );
  }

  /**
   * Gets dwspay account info for a winery
   */
  getDwspayAccount(wineryId: string): Observable<{
    enabled: boolean;
    connected: boolean;
    stripe_account_connect_id?: string;
  }> {
    return resource('crm://wspayments/accounts/winery/id/dwspay')
      .params({ id: wineryId })
      .get<{
        enabled: boolean;
        connected: boolean;
        stripe_account_connect_id?: string;
      }>();
  }

  /**
   * Creates a Stripe Connect account session for embedded components (dwspay)
   * @param wineryId - The winery ID
   * @param components - Optional components to enable (balances, account_management, payments, etc.)
   */
  createAccountSession(
    wineryId: string,
    components?: {
      balances?: { enabled: boolean; features?: { instant_payouts?: boolean; standard_payouts?: boolean; edit_payout_schedule?: boolean; external_account_collection?: boolean } };
      account_management?: { enabled: boolean };
      payments?: { enabled: boolean; features?: { refund_management?: boolean; dispute_management?: boolean; capture_payments?: boolean } };
      payment_details?: {
        enabled: boolean;
        features?: {
          refund_management?: boolean;
          dispute_management?: boolean;
          capture_payments?: boolean;
          destination_on_behalf_of_charge_management?: boolean;
        };
      };
      payouts_list?: { enabled: boolean; features?: { instant_payouts?: boolean; standard_payouts?: boolean; edit_payout_schedule?: boolean; external_account_collection?: boolean } };
      payment_disputes?: { enabled: boolean; features?: { refund_management?: boolean; dispute_management?: boolean; destination_on_behalf_of_charge_management?: boolean } };
      issuing_cards_list?: { enabled: boolean; features?: { card_management?: boolean; cardholder_management?: boolean; card_spend_dispute_management?: boolean; spend_control_management?: boolean } };
      onboarding?: { enabled: boolean };
    },
    forOnboarding?: boolean,
    accountConfiguration?: {
      controller?: {
        fees?: { payer: string };
        stripe_dashboard?: { type: string };
        losses?: { payments: string };
        requirement_collection?: string;
      };
      capabilities?: {
        transfers?: { requested: boolean };
        card_issuing?: { requested: boolean };
      };
    },
    /** Use payment-details–only account session (ConnectJS) — refund/capture/destination; disputes off. */
    options?: { forPaymentDetailsEmbed?: boolean }
  ): Observable<{ clientSecret: string; publishableKey?: string }> {
    const payload: any = { wineryId };
    if (components) {
      payload.components = components;
    }
    if (forOnboarding) {
      payload.forOnboarding = true;
    }
    if (accountConfiguration) {
      payload.accountConfiguration = accountConfiguration;
    }
    if (options?.forPaymentDetailsEmbed) {
      payload.forPaymentDetailsEmbed = true;
    }
    // Use dwspay endpoint for account sessions (correct routing for dwspay integration)
    return resource('crm://wspayments/dwspay/connect/account-sessions')
      .post<{ clientSecret: string; publishableKey?: string }>(payload);
  }

  /**
   * Refunds a dwspay payment intent (full or partial).
   */
  refundDwspayPaymentIntent(
    wineryId: string,
    paymentIntentId: string,
    options?: { reason?: string; notes?: string; amountCents?: number }
  ): Observable<{ paymentIntent: Record<string, any>; refund: Record<string, any> }> {
    const payload: { wineryId: string; reason?: string; notes?: string; amountCents?: number } = { wineryId };
    if (options?.reason) {
      payload.reason = options.reason;
    }
    if (options?.notes) {
      payload.notes = options.notes;
    }
    if (options?.amountCents != null && options.amountCents > 0) {
      payload.amountCents = options.amountCents;
    }
    return resource('crm://wspayments/dwspay/payment-intent/id/refund')
      .params({ id: paymentIntentId })
      .post(payload);
  }

  /**
   * Creates a new dwspay Stripe account with JSON configuration and returns account session for onboarding
   * @param wineryId - The winery ID
   * @param accountConfiguration - Account configuration with controller and capabilities
   * @param returnUrl - Optional return URL from window.location.href for redirect after onboarding
   * @param tosAcceptanceTimestampEpochSec - Optional ToS acceptance timestamp from agreements (ws-payment). When set, used for Stripe tos_acceptance (WS-2376).
   */
  createDwspayAccount(
    wineryId: string,
    accountConfiguration: {
      controller?: {
        fees?: { payer: string };
        stripe_dashboard?: { type: string };
        losses?: { payments: string };
        requirement_collection?: string;
      };
      capabilities?: {
        transfers?: { requested: boolean };
        card_issuing?: { requested: boolean };
      };
    },
    returnUrl?: string,
    tosAcceptanceTimestampEpochSec?: number | null
  ): Observable<{
    accountId: string;
    stripeAccountConnectId?: string;
    clientSecret: string;
    publishableKey?: string;
    expressLink?: string;
  }> {
    const body: any = {
      wineryId,
      accountConfiguration,
      returnUrl
    };
    if (tosAcceptanceTimestampEpochSec != null && tosAcceptanceTimestampEpochSec > 0) {
      body.tosAcceptanceTimestampEpochSec = tosAcceptanceTimestampEpochSec;
    }
    return resource('crm://wspayments/accounts/dwspay/create')
      .post<{
        accountId: string;
        stripeAccountConnectId?: string;
        clientSecret: string;
        publishableKey?: string;
        expressLink?: string;
      }>(body);
  }

  /**
   * Resumes onboarding for an existing dwspay Stripe account
   * Uses AccountLink for redirect-based onboarding (external Stripe page)
   * @param wineryId - The winery ID
   * @param accountId - The existing Stripe account ID to resume onboarding for
   * @param accountConfiguration - Account configuration with controller and capabilities
   * @param returnUrl - Optional return URL from window.location.href for redirect after onboarding
   */
  resumeDwspayOnboarding(
    wineryId: string,
    accountId: string,
    accountConfiguration: {
      controller?: {
        fees?: { payer: string };
        stripe_dashboard?: { type: string };
        losses?: { payments: string };
        requirement_collection?: string;
      };
      capabilities?: {
        transfers?: { requested: boolean };
        card_issuing?: { requested: boolean };
      };
    },
    returnUrl?: string
  ): Observable<{ clientSecret: string; publishableKey: string }> {
    // Add returnUrl and refreshUrl to accountConfiguration based on current environment
    const origin = window.location.origin;
    const configWithUrls = {
      ...accountConfiguration,
      returnUrl: returnUrl || `${origin}/payments`,
      refreshUrl: `${origin}/payments/create-account`
    };

    // Use stripe-connect endpoint for resume (returns AccountLink URL for redirect)
    // Note: "clientSecret" is actually an AccountLink URL for backwards compatibility
    return resource('crm://wspayments/stripe-connect/account-sessions')
      .post<{ clientSecret: string; publishableKey: string }>({
        wineryId,
        accountId,
        forOnboarding: true,
        accountConfiguration: configWithUrls
      });
  }
}

/**
 * WSPayments links Stripe sales to a CRM order using {@code sale_rows.entity_type} / {@code entity_id}.
 * B2C checkouts use metadata entityType ORDER (→ {@code order}); classic POS preorder flows use {@code preorder}.
 */
export function wspaymentsSaleEntityTypeForCrmOrder(order: {
  orderType?: string;
  data?: unknown;
}): 'order' | 'preorder' {
  const data = order.data;
  if (data && typeof data === 'object') {
    const ext = (data as { sourceExternalType?: string }).sourceExternalType;
    if (typeof ext === 'string' && ext.trim().toLowerCase() === 'divinea-b2c') {
      return 'order';
    }
    const um = (data as { userMetadata?: { b2cProvider?: string } }).userMetadata;
    if (
      um &&
      typeof um === 'object' &&
      typeof um.b2cProvider === 'string' &&
      um.b2cProvider.toLowerCase().includes('wsblocks')
    ) {
      return 'order';
    }
  }
  if (order.orderType === 'direct' || order.orderType === 'ecommerce' || order.orderType === 'order') {
    return 'order';
  }
  return 'preorder';
}

/** Sale row keys to try when resolving WSPayments for a CRM order (promoted self-orders move payments to entity_type order + numeric id). */
export function wspaymentsLookupKeysForCrmOrder(order: {
  id?: string | number;
  code?: string;
  orderType?: string;
  data?: unknown;
  /** V4 / promoted preorder: source preorder uuid when {@code code} is empty. */
  externalId?: string;
}): Array<{ saleEntityType: string; saleEntityId: string }> {
  const keys: Array<{ saleEntityType: string; saleEntityId: string }> = [];
  const add = (saleEntityType: string, saleEntityId: string | number | undefined | null) => {
    if (saleEntityId === undefined || saleEntityId === null) {
      return;
    }
    const id = String(saleEntityId).trim();
    if (!id) {
      return;
    }
    if (keys.some((k) => k.saleEntityType === saleEntityType && k.saleEntityId === id)) {
      return;
    }
    keys.push({ saleEntityType, saleEntityId: id });
  };

  if (order.code) {
    add(wspaymentsSaleEntityTypeForCrmOrder(order), order.code);
    add('preorder', order.code);
  }
  if (order.externalId) {
    add('preorder', order.externalId);
    add('order', order.externalId);
  }
  if (order.id != null) {
    add('order', order.id);
  }
  if (order.code) {
    add('order', order.code);
  }

  const data = order.data;
  if (data && typeof data === 'object') {
    const preorderIds = (data as { preorderIds?: unknown }).preorderIds;
    if (Array.isArray(preorderIds)) {
      for (const pid of preorderIds) {
        add('preorder', pid as string | number);
      }
    }
  }

  return keys;
}

export interface PaymentFilters {
  saleEntityType?: string;
  saleEntityId?: string;

  page?: number;
  size?: number;
}

export interface PaymentLinkFilters {
  reservationId?: string;
  wineryId?: string;
  manualOnly?: boolean;
  linkStatus?: string;

  page?: number;
  size?: number;
}

export interface ManualPaymentLinkRequest {
  wineryId: string;
  amountCents: number;
  currency?: string;
  description: string;
  contactId?: number;
  customerName?: string;
  customerEmail?: string;
  locale?: string;
  clientIp?: string;
}

export interface PaymentLinkEmbeddedSetup {
  clientSecret: string;
  publishableKey: string;
}

export interface RegeneratePaymentLinkRequest {
  wineryId: string;
  amountCents: number;
  locale?: string;
}

/** Printer type — drives client print path (default cloudprnt). */
export type WsposThermalPrinterType = 'cloudprnt' | 'escpos-9100' | 'qz-tray';

/** Row from GET /dwspay/wspos/thermal-printers */
export interface WsposThermalPrinter {
  id: string;
  name: string;
  type?: WsposThermalPrinterType | string;
  wineryId?: string;
  pointOfSaleWineSuiteId?: string | null;
  macAddress?: string | null;
  username?: string;
  serverUrl?: string;
  url?: string;
  printerUrl?: string;
  config?: Record<string, unknown> | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** Present only on create / rotate-credentials (CloudPRNT) */
  password?: string;
}

export interface CreateWsposThermalPrinterRequest {
  wineryId: string;
  name: string;
  type?: WsposThermalPrinterType | string;
  /** Required for escpos-9100 / qz-tray */
  printerUrl?: string;
  macAddress?: string;
  pointOfSaleWineSuiteId?: string;
  config?: Record<string, unknown>;
}

export interface UpdateWsposThermalPrinterRequest {
  name?: string;
  type?: WsposThermalPrinterType | string;
  printerUrl?: string;
  macAddress?: string;
  pointOfSaleWineSuiteId?: string;
  clearPointOfSale?: boolean;
  config?: Record<string, unknown>;
  isActive?: boolean;
}

export type WsposThermalPrinterCredentialsResponse = WsposThermalPrinter & {
  password?: string;
  serverUrl?: string;
  username?: string;
};

/** Terminal list item from wspayments/dwspay/terminal/terminals (same as used by POS). */
export interface WspaymentsTerminalListItem {
  description?: string;
  terminalId: string;
  locationId?: string;
  /** Point of sale name (camelCase from API). */
  pointOfSale?: string;
  /** Point of sale name (snake_case, when API returns it). */
  point_of_sale?: string;
}

export interface TerminalReaderDetail {
  id: string;
  object: string;
  label?: string;
  device_type?: string;
  device_sw_version?: string;
  status?: string;
  serial_number?: string;
  ip_address?: string;
  location?: string;
  last_seen_at?: number;
  livemode?: boolean;
  metadata?: Record<string, string>;
}

/** Server-side list query for Wine Suite payments tab. */
export interface WinesuitePaymentsListQuery {
  search?: string;
  status?: string;
  customerName?: string;
  customerEmail?: string;
  saleId?: string;
  mode?: string;
  origin?: string;
  amountMin?: number;
  amountMax?: number;
  /** Unix epoch seconds (inclusive). */
  dateFrom?: number;
  dateTo?: number;
  /** created date sort: asc | desc (default desc). */
  sortDir?: 'asc' | 'desc';
}

/** Summary row from GET /dwspay/winesuite/payments. */
export interface DwspayPaymentSummary {
  id: string;
  amount?: number;
  currency?: string;
  customer?: string;
  description?: string;
  metadata?: Record<string, string>;
  status?: string;
  paymentMethodType?: string;
  cardBrand?: string;
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  /** Unix epoch milliseconds. */
  created?: number;
  refunded?: boolean;
  amountRefunded?: number;
  amount_refunded?: number;
  /** Connected-account Stripe payment id (py_) for Connect embedded refund UI */
  connectPaymentId?: string;
  applicationFeeTotal?: number;
  commissioneWspayments?: number;
  commissioneDivineaCom?: number;
  payoutId?: string;
  payoutAmount?: number;
  payoutDate?: number;
  payout_id?: string;
  payout_amount?: number | string;
  payout_date?: number | string;
}

export interface DwspayPaymentIntentDetails {
  paymentIntent?: Record<string, any>;
  id?: string;
  amount?: number;
  currency?: string;
  status?: string;
  connectPaymentId?: string;
  applicationFeeTotal?: number;
  commissioneWspayments?: number;
  commissioneDivineaCom?: number;
  amountRefunded?: number;
  /** Unix epoch milliseconds — payment row {@code created_at} when available. */
  created?: number;
  refunds?: Array<{ id?: string; amount?: number; created?: number; reason?: string; status?: string }>;
  /** Settlement currency amount from Stripe BalanceTransaction (cents). */
  settlementAmount?: number;
  settlementCurrency?: string;
  settlementFee?: number;
  /** Stripe FX rate when presentment currency ≠ settlement currency. */
  exchangeRate?: number;
}

export interface ManualDwspayPaymentIntentRequest {
  wineryId: string;
  saleId: string;
  amount: number;
  currency: string;
  captureMethod: 'automatic' | 'manual';
  moto?: boolean;
  metadata: { [key: string]: string };
}

export interface ManualDwspayPaymentIntentResponse {
  id?: string;
  status?: string;
  client_secret?: string;
  clientSecret?: string;
  [key: string]: any;
}

export type AdvancedExportMode = 'PAYMENTS' | 'PAYOUTS' | 'SINGLE_PAYOUT';

/** Tab context for advanced export — export is always unified (payments + payout columns). */
export type AdvancedExportScope = 'payments' | 'payouts';

export interface AdvancedExportRow {
  stripe_id: string;
  date: number;
  status: 'succeeded' | 'failed' | 'refunded' | string;
  amount: number;
  amount_refunded: number;
  currency: string;
  total_fee: number;
  wspayments_fee: number;
  divinea_fee: number;
  description: string;
  customer_name: string;
  customer_email: string;
  sale_id: string;
  entity_type: string;
  transaction_mode: 'card_present' | 'card_not_present' | string;
  payout_id: string;
  payout_amount: number | string;
  payout_date: number | string;
}

export interface AdvancedExportResponse {
  rows: AdvancedExportRow[];
  count: number;
  wineryId: string;
  dateFrom: string;
  dateTo: string;
  mode: AdvancedExportMode;
}

/** Response from GET /dwspay/winesuite/payment-intent/{id} (same JSON as terminal retrieval). */
export interface DwspayPaymentIntentDetailsResponse {
  id?: string;
  amount?: number;
  currency?: string;
  status?: string;
  paymentIntent?: Record<string, any>;
  connectPaymentId?: string;
  applicationFeeTotal?: number;
  commissioneWspayments?: number;
  commissioneDivineaCom?: number;
  amountRefunded?: number;
  amount_refunded?: number;
  /** Unix epoch milliseconds — payment row {@code created_at} when available. */
  created?: number;
  refunds?: Array<{ id?: string; amount?: number; created?: number; reason?: string; status?: string }>;
  /** Settlement currency amount from Stripe BalanceTransaction (cents). */
  settlementAmount?: number;
  settlementCurrency?: string;
  settlementFee?: number;
  /** Stripe FX rate when presentment currency ≠ settlement currency. */
  exchangeRate?: number;
  error?: string;
}

export interface WinesuitePaymentsPage {
  payments: DwspayPaymentSummary[];
  page: number;
  size: number;
  totalElements: number;
}

export interface DwspayPaymentsFilter {
  customerName?: string;
  customerEmail?: string;
  saleId?: string;
  status?: string;
  transactionMode?: string;
  origin?: string;
  amountMin?: number;
  amountMax?: number;
  searchText?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface DwspayPaymentQueryRow {
  stripe_id?: string;
  date?: number;
  status?: string;
  amount?: number;
  amount_refunded?: number;
  currency?: string;
  total_fee?: number;
  wspayments_fee?: number;
  divinea_fee?: number;
  customer_name?: string;
  customer_email?: string;
  sale_id?: string;
  entity_type?: string;
  transaction_mode?: string;
  payment_method_type?: string;
  origin?: string;
}

export interface DwspayPaymentsQueryResponse {
  content?: DwspayPaymentQueryRow[];
  page?: number;
  count?: number;
  total?: number;
  totalPages?: number;
}

export interface WinesuitePayoutsListQuery {
  search?: string;
  payoutId?: string;
  destination?: string;
  status?: string;
  method?: string;
  amountMin?: number;
  amountMax?: number;
  dateFrom?: number;
  dateTo?: number;
  sortDir?: 'asc' | 'desc';
}

export interface DwspayPayoutsFilter {
  payoutId?: string;
  destination?: string;
  status?: string;
  method?: string;
  amountMin?: number;
  amountMax?: number;
  searchText?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface WinesuitePayoutRow {
  id: string;
  created?: number;
  arrival_date?: number;
  status?: 'paid' | 'in_transit' | string;
  stripe_status?: string;
  amount?: number;
  currency?: string;
  method?: 'standard' | 'express' | string;
  destination_bank_name?: string;
  destination_last4?: string;
  destination?: string;
  statement_descriptor?: string;
  tracking_id?: string;
}

export interface WinesuitePayoutsPageResponse {
  content?: WinesuitePayoutRow[];
  page?: number;
  count?: number;
  total?: number;
  totalPages?: number;
}

export interface WinesuitePayoutsPage {
  payouts: WinesuitePayoutRow[];
  page: number;
  size: number;
  totalElements: number;
}

export interface WinesuitePayoutTimelineEvent {
  key: 'paid' | 'in_transit' | 'automatic' | string;
  at?: number;
  active?: boolean;
}

export interface WinesuitePayoutSummaryLine {
  type: 'charges' | 'refunds' | 'adjustments' | 'payouts' | string;
  count?: number;
  gross?: number;
  fees?: number;
  total?: number;
}

export interface WinesuitePayoutTransactionRow {
  type: 'payout' | 'payment' | 'refund' | 'adjustment' | string;
  amount?: number;
  currency?: string;
  customer_name?: string;
  sale_id?: string;
  entity_type?: string;
  payment_intent_id?: string;
  navigable?: boolean;
}

export interface WinesuitePayoutDetail {
  payout?: WinesuitePayoutRow;
  timeline?: WinesuitePayoutTimelineEvent[];
  in_transit_until?: number;
  summary?: WinesuitePayoutSummaryLine[];
  wspayments_fee?: number;
  divinea_fee?: number;
  total_fee?: number;
  transactions?: WinesuitePayoutTransactionRow[];
}